/**
 * Feedback Service
 *
 * API service for submitting bug reports
 */

import { apiClient } from '@/lib/api-client'
import type { BugReportRequest, BugReportResponse } from '../types/feedback.types'

/**
 * Append a value to FormData if it exists
 */
function appendIfPresent(formData: FormData, key: string, value: string | undefined): void {
  if (value) {
    formData.append(key, value)
  }
}

/**
 * Submit a bug report to the backend
 */
export async function submitBugReport(data: BugReportRequest): Promise<BugReportResponse> {
  const formData = new FormData()

  // Required fields
  formData.append('title', data.title)
  formData.append('severity', data.severity)
  formData.append('page_url', data.page_url)
  formData.append('user_agent', data.user_agent)
  formData.append('viewport_size', data.viewport_size)

  // Optional string fields
  appendIfPresent(formData, 'description', data.description)
  appendIfPresent(formData, 'steps_to_reproduce', data.steps_to_reproduce)
  appendIfPresent(formData, 'deal_id', data.deal_id)
  appendIfPresent(formData, 'deal_name', data.deal_name)
  appendIfPresent(formData, 'console_errors', data.console_errors)
  appendIfPresent(formData, 'deal_diligence_snapshot', data.deal_diligence_snapshot)

  // File attachments
  if (data.screenshot) {
    formData.append('screenshot', data.screenshot, 'screenshot.png')
  }
  if (data.attachments && data.attachments.length > 0) {
    // FastAPI expects multiple files with the same field name to be collected as a list
    for (const file of data.attachments) {
      formData.append('attachments', file, file.name)
    }
  }

  return apiClient.post<BugReportResponse>('/api/v1/feedback/bug-report', formData)
}
